import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Grid,
  Box,
  Typography,
  Divider,
  CircularProgress,
  Chip,
  Stack
} from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { Disease, DiseaseCreate, DiseaseUpdate } from '../../../types/disease';
import { useFormValidation } from '../../../hooks/useFormValidation';
import { useAuth } from '../../../hooks/useAuth';
import { useEnvironment } from '../../../hooks/useEnvironment';
import ErrorMessage from '../../common/ErrorMessage/ErrorMessage';
import ConfirmationDialog from '../../safety/ConfirmationDialog/ConfirmationDialog';
import ReviewChangesDialog from '../../safety/ReviewChangesDialog/ReviewChangesDialog';
import { mapErrorToMessage } from '../../../utils/error-mapping';

interface DiseaseFormProps {
  disease?: Disease | null;
  onSave: (data: DiseaseCreate | DiseaseUpdate, id?: string) => Promise<void>;
  onDelete?: (id: string) => Promise<void>;
  onCancel?: () => void;
}

const DiseaseForm: React.FC<DiseaseFormProps> = ({
  disease,
  onSave,
  onDelete,
  onCancel
}) => {
  const { user } = useAuth();
  const { environment } = useEnvironment();
  const isViewer = user?.role === 'viewer';
  const isProduction = environment === 'production';

  const [formData, setFormData] = useState<DiseaseCreate>({
    name: '',
    mesh_id: '',
    content_archive_url: '',
    description: '',
    summary_description: '',
    scope_note: '',
    synonyms: [],
    tree_numbers: [],
    completion_score: 0,
  });

  const [originalData, setOriginalData] = useState<Disease | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [synonymInput, setSynonymInput] = useState('');
  const [treeNumberInput, setTreeNumberInput] = useState('');

  const { errors, validateAll, clearAllErrors } = useFormValidation({
    name: { required: true, minLength: 1 }
  });

  useEffect(() => {
    if (disease) {
      const data = {
        name: disease.name || '',
        mesh_id: disease.mesh_id || '',
        content_archive_url: disease.content_archive_url || '',
        description: disease.description || '',
        summary_description: disease.summary_description || '',
        scope_note: disease.scope_note || '',
        synonyms: disease.synonyms || [],
        tree_numbers: disease.tree_numbers || [],
        completion_score: disease.completion_score || 0,
      };
      setFormData(data);
      setOriginalData(disease);
    } else {
      setOriginalData(null);
    }
  }, [disease]);

  const handleChange = (field: keyof DiseaseCreate, value: any) => {
    setFormData((prev: DiseaseCreate) => ({ ...prev, [field]: value }));
  };

  const handleAddSynonym = () => {
    if (synonymInput.trim()) {
      setFormData((prev: DiseaseCreate) => ({
        ...prev,
        synonyms: [...(prev.synonyms || []), synonymInput.trim()]
      }));
      setSynonymInput('');
    }
  };

  const handleRemoveSynonym = (index: number) => {
    setFormData((prev: DiseaseCreate) => ({
      ...prev,
      synonyms: (prev.synonyms || []).filter((_: string, i: number) => i !== index)
    }));
  };

  const handleAddTreeNumber = () => {
    if (treeNumberInput.trim()) {
      setFormData((prev: DiseaseCreate) => ({
        ...prev,
        tree_numbers: [...(prev.tree_numbers || []), treeNumberInput.trim()]
      }));
      setTreeNumberInput('');
    }
  };

  const handleRemoveTreeNumber = (index: number) => {
    setFormData((prev: DiseaseCreate) => ({
      ...prev,
      tree_numbers: (prev.tree_numbers || []).filter((_: string, i: number) => i !== index)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateAll(formData)) {
      return;
    }

    if (isProduction) {
      setShowReviewDialog(true);
      return;
    }

    await performSave();
  };

  const performSave = async () => {
    setLoading(true);
    setError(null);
    clearAllErrors();
    setShowReviewDialog(false);

    try {
      await onSave(formData, disease?.id);
    } catch (err: any) {
      setError(mapErrorToMessage(err, 'Failed to save disease'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!disease?.id || !onDelete) return;

    setLoading(true);
    setError(null);

    try {
      await onDelete(disease.id);
      setShowDeleteConfirm(false);
    } catch (err: any) {
      setError(mapErrorToMessage(err, 'Failed to delete disease'));
      setShowDeleteConfirm(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {/* Header with Title and Delete Button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2">
          {disease ? 'Edit Disease' : 'New Disease'}
        </Typography>
        {disease && onDelete && !isViewer && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setShowDeleteConfirm(true)}
            disabled={loading}
          >
            Delete Disease
          </Button>
        )}
      </Box>

      {error && <ErrorMessage message={error} onDismiss={() => setError(null)} />}

      <form onSubmit={handleSubmit}>
        {/* Basic Information Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Basic Information
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                disabled={isViewer}
                error={!!errors.name}
                helperText={errors.name}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="ID"
                value={disease?.id || ''}
                disabled
                helperText="Unique identifier (auto-generated)"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="MeSH ID"
                value={formData.mesh_id}
                onChange={(e) => handleChange('mesh_id', e.target.value)}
                disabled={isViewer}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Content Archive URL"
                value={formData.content_archive_url}
                onChange={(e) => handleChange('content_archive_url', e.target.value)}
                disabled={isViewer}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Description Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Description
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={6}
                label="Summary Description"
                value={formData.summary_description}
                onChange={(e) => handleChange('summary_description', e.target.value)}
                disabled={isViewer}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={10}
                label="Detailed Description"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                disabled={isViewer}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={6}
                label="Scope Note (MeSH Summary)"
                value={formData.scope_note}
                onChange={(e) => handleChange('scope_note', e.target.value)}
                disabled={isViewer}
              />
            </Grid>
          </Grid>
        </Box>

        {/* MeSH Data Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            MeSH Data
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Synonyms
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  value={synonymInput}
                  onChange={(e) => setSynonymInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddSynonym())}
                  placeholder="Enter a synonym..."
                  disabled={isViewer}
                />
                <Button
                  variant="contained"
                  onClick={handleAddSynonym}
                  disabled={isViewer || !synonymInput.trim()}
                  startIcon={<AddIcon />}
                >
                  Add
                </Button>
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {formData.synonyms?.map((synonym, index) => (
                  <Chip
                    key={index}
                    label={synonym}
                    onDelete={!isViewer ? () => handleRemoveSynonym(index) : undefined}
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Stack>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Tree Numbers
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  value={treeNumberInput}
                  onChange={(e) => setTreeNumberInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTreeNumber())}
                  placeholder="Enter a tree number..."
                  disabled={isViewer}
                />
                <Button
                  variant="contained"
                  onClick={handleAddTreeNumber}
                  disabled={isViewer || !treeNumberInput.trim()}
                  startIcon={<AddIcon />}
                >
                  Add
                </Button>
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {formData.tree_numbers?.map((treeNumber, index) => (
                  <Chip
                    key={index}
                    label={treeNumber}
                    onDelete={!isViewer ? () => handleRemoveTreeNumber(index) : undefined}
                    color="secondary"
                    variant="outlined"
                  />
                ))}
              </Stack>
            </Grid>
          </Grid>
        </Box>

        {/* Additional Information Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Additional Information
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Completion Score (0-100)"
                value={formData.completion_score}
                onChange={(e) => handleChange('completion_score', parseFloat(e.target.value) || 0)}
                disabled={isViewer}
                inputProps={{ min: 0, max: 100, step: 0.1 }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Form Actions */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
          {onCancel && (
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={onCancel}
              size="large"
            >
              Cancel
            </Button>
          )}
          {!isViewer && (
            <Button
              type="submit"
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              disabled={loading}
              size="large"
            >
              {loading ? 'Saving...' : 'Save'}
            </Button>
          )}
        </Box>
      </form>

      <ConfirmationDialog
        isOpen={showDeleteConfirm}
        title="Delete Disease"
        message={`Are you sure you want to delete "${disease?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteConfirm(false)}
        isDangerous={true}
        requireDoubleConfirm={isProduction}
        doubleConfirmMessage={`You are about to permanently delete "${disease?.name}" from the PRODUCTION database. This action is irreversible and cannot be undone.`}
      />

      <ReviewChangesDialog
        isOpen={showReviewDialog}
        title="Review Production Changes"
        originalData={originalData}
        modifiedData={formData}
        onConfirm={performSave}
        onCancel={() => setShowReviewDialog(false)}
        entityType="disease"
      />
    </Box>
  );
};

export default DiseaseForm;
